import { difference } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { shallow } from 'zustand/shallow';

import { useWatch } from '@/package';
import { useLabelingBoxContext } from '@/provider';
import { useTaskFrameStore } from '@/store';
import { type FrameData, type Task } from '@/type';

import { useFrameDetailQuery } from '../frame';
import useFramePcdFileList from './useFramePcdFileList';

type SequentialFrameFetcherProps = {
  frameSeq: FrameData['frameSeq'];
  taskID: Task['id'];
};

const useSequentialFrameFetcher = ({
  frameSeq,
  taskID,
}: SequentialFrameFetcherProps) => {
  const [addedFrameList, setAddedFrameList] = useState([] as FrameData[]);
  const [allFrameList, selectedTask] = useTaskFrameStore(
    state => [state.allFrameList, state.selectedTask],
    shallow,
  );

  const count = selectedTask?.fileFrameCount ?? 0;
  const workFrameInterval = Math.max(selectedTask?.workFrameInterval ?? 1, 1);

  const fetchingSeq = useMemo(() => {
    const maxPreloadSeq = Math.min(frameSeq + 10 * workFrameInterval, count);

    for (let seq = frameSeq; seq <= maxPreloadSeq; seq += workFrameInterval) {
      if (!allFrameList[seq - 1]) return seq;
    }

    return 0;
  }, [allFrameList, count, frameSeq, workFrameInterval]);

  const { data: frameData } = useFrameDetailQuery({
    frameSeq: fetchingSeq,
    taskID,
  });

  const { pcdIndexBoxListMap } = useLabelingBoxContext();

  useFramePcdFileList({ frameList: addedFrameList, frameSeq });

  useWatch(
    allFrameList.map(frame => frame?.frameSeq),
    (newList, oldList) => {
      const addedList = difference(newList, oldList).filter(v => v);

      if (!addedList.length) return;

      setAddedFrameList(addedList.map(seq => allFrameList[seq - 1]));
    },
    {
      equalityFn: shallow,
    },
  );

  useEffect(() => {
    if (!frameData) return;

    const timer = setTimeout(() => {
      const index = frameData.frameSeq - 1;

      useTaskFrameStore.getState().setFrame(frameData, index);
    }, 200);

    return () => clearTimeout(timer);
  }, [frameData, pcdIndexBoxListMap]);
};

export default useSequentialFrameFetcher;
